// hooks/usePlanSelection.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import { openAuthDialog } from "../store/auth/authSlice";
import {
  createSubscription,
  updateSubscription,
} from "../store/membership/membershipActions";
import { setIsPlanProcessing } from "../store/membership/membershipSlice";
import { refreshUser, getUserSubscription } from "../store/auth/authActions";
import {
  getEffectivePlan,
  isDowngradePending,
  getDowngradeTargetPlan,
  getTimeUntilDowngrade,
} from "../utils/subscriptionUtils";

export const usePlanSelection = (plans) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const dispatch = useDispatch();

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isDowngradeConfirmModalOpen, setIsDowngradeConfirmModalOpen] =
    useState(false);
  const [pendingDowngradePlan, setPendingDowngradePlan] = useState(null);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningModalData, setWarningModalData] = useState({
    title: "",
    message: "",
  });

  const { isAuthenticated, user, currentSubscription, defaultPaymentMethod } =
    useSelector((state) => state.auth);
  const { isPlanProcessing } = useSelector((state) => state.membership);

  const showWarningModal = (title, message) => {
    setWarningModalData({ title, message });
    setIsWarningModalOpen(true);
  };

  const handleWarningModalClose = () => {
    setIsWarningModalOpen(false);
    setWarningModalData({ title: "", message: "" });
  };

  const performDowngrade = async (plan) => {
    try {
      const payload = {
        subscriptionId: currentSubscription?.stripeSubscriptionId,
        newPriceId: plan.stripePriceId,
        paymentMethodId: defaultPaymentMethod?.id,
        isDowngrade: true,
      };

      await dispatch(updateSubscription({ payload })).unwrap();

      const userPlan = getEffectivePlan(currentSubscription);

      let message = "";
      if (userPlan === "premium" && (plan.id === "pro" || plan.id === "free")) {
        message = `Your subscription will be downgraded to the **${
          plan.name || plan.id
        }** plan starting from your next billing cycle.`;
      } else if (userPlan === "pro" && plan.id === "free") {
        message = `You will move to the **Free** plan at the beginning of your next billing cycle.`;
      }

      toast({
        title: "Plan Change Scheduled",
        description: message,
        variant: "success",
        duration: 3000,
      });

      await dispatch(refreshUser()).unwrap();
      await dispatch(getUserSubscription(user.uid)).unwrap();
      navigate("/dashboard");
    } catch (error) {
      console.error("Downgrade failed:", error);
      toast({
        title: "Downgrade Failed",
        description:
          "There was an error processing your downgrade. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDowngrade = async () => {
    if (!pendingDowngradePlan) return;
    dispatch(setIsPlanProcessing(true));
    try {
      await performDowngrade(pendingDowngradePlan);
    } finally {
      dispatch(setIsPlanProcessing(false));
      setIsDowngradeConfirmModalOpen(false);
      setPendingDowngradePlan(null);
    }
  };

  const handlePlanSelection = async (planId, planName) => {
    if (isPlanProcessing) return;

    try {
      if (!isAuthenticated) {
        toast({
          title: "Authentication Required",
          description: "Please log in or sign up as a seller to select a plan.",
          variant: "destructive",
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: false,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: (role) => {
              if (role === "seller")
                navigate("/list-inventory", { replace: true });
            },
            defaultTabOverride: "signup",
          }),
        );
        return;
      }

      if (user && user.role !== "seller") {
        toast({
          title: "Seller Account Required",
          description:
            "Membership plans are for seller accounts. Please sign up as a seller or contact support.",
          variant: "destructive",
        });
        return;
      }

      if (!plans?.length) {
        toast({
          title: "Plans Not Loaded",
          description: "Please wait a moment and try again.",
          variant: "destructive",
        });
        return;
      }

      const plan = plans.find((p) => p.id === planId);
      if (!plan) {
        toast({
          title: "Plan Not Found",
          description: "The selected plan is invalid. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const isTrialPeriodActive = currentSubscription?.trialEnd
        ? new Date(currentSubscription.trialEnd) > new Date()
        : false;

      const userPlan = isTrialPeriodActive
        ? getEffectivePlan(currentSubscription)
        : currentSubscription?.planId;

      const alreadyDowngrade = isTrialPeriodActive
        ? (getDowngradeTargetPlan(currentSubscription) ?? "")
        : "";

      // Determine if this user is trial-eligible for the chosen plan.
      // hasUsedTrial covers users who downgraded after a paid plan —
      // they have isTrial:false and trialExpired:false but must not see the trial flow again.
      const isTrialEligible =
        user?.role === "seller" &&
        (userPlan === "free" || !userPlan) &&
        !user?.membership?.isTrial &&
        !user?.membership?.trialExpired &&
        !user?.hasUsedTrial &&
        planId !== "free";

      // Trial-eligible users: route to upgrade page with isTrial flag (no payment)
      if (isTrialEligible && (planId === "pro" || planId === "premium")) {
        navigate("/upgrade-membership", {
          state: { newPlan: plan, isTrial: true },
        });
        return;
      }

      // Standard upgrade paths (paid)
      if (userPlan === "free" && (planId === "pro" || planId === "premium")) {
        navigate("/upgrade-membership", { state: { newPlan: plan } });
        return;
      }

      if (userPlan === "pro" && planId === "premium") {
        navigate("/upgrade-membership", { state: { newPlan: plan } });
        return;
      }

      if (
        getEffectivePlan(currentSubscription) === "premium" &&
        getDowngradeTargetPlan(currentSubscription) === "free" &&
        (planId === "pro" || planId === "premium") &&
        isTrialPeriodActive
      ) {
        showWarningModal(
          "Action Not Allowed",
          `You've already downgraded from Premium to Free.\n\nYou'll be able to upgrade/downgrade to ${planId} once your Free plan is fully active and the current quota period has ended.`,
        );
        return;
      }

      if (alreadyDowngrade === planId) {
        toast({
          title: "Plan Already Downgraded",
          description: `You've already downgraded your plan to the ${planId} Tier.`,
          variant: "info",
          duration: 2500,
        });
        return;
      }

      if (
        (userPlan === "premium" && (planId === "pro" || planId === "free")) ||
        (userPlan === "pro" && planId === "free")
      ) {
        setPendingDowngradePlan(plan);
        setIsDowngradeConfirmModalOpen(true);
        return;
      }

      dispatch(setIsPlanProcessing(true));
      setSelectedPlanId(planId);

      if (plan.id === "free") {
        toast({
          title: `Plan Selected: ${planName}`,
          description: "Activating your free plan...",
          variant: "success",
          duration: 2000,
        });

        await dispatch(
          createSubscription({
            payload: { userId: user.uid, planId: plan.id },
          }),
        ).unwrap();

        await dispatch(refreshUser()).unwrap();
        await dispatch(getUserSubscription(user.uid)).unwrap();

        toast({
          title: "Plan Activated!",
          description: `Your ${planName} plan is now active. Redirecting to your dashboard...`,
          variant: "success",
          duration: 2000,
        });

        navigate("/dashboard", { state: { plan: planId, planSelected: true } });
      } else {
        navigate("/upgrade-membership", { state: { newPlan: plan } });
      }
    } catch (error) {
      console.error("Plan activation failed:", error);
      toast({
        title: "Activation Failed",
        description:
          "There was an error activating your plan. Please try again.",
        variant: "destructive",
      });
      setSelectedPlanId(null);
    } finally {
      dispatch(setIsPlanProcessing(false));
    }
  };

  const handleLearnMore = (planId) => {
    if (!isAuthenticated) {
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: false,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () =>
            navigate("/list-inventory", {
              replace: true,
              state: { scrollToPlan: planId },
            }),
          defaultTabOverride: "signup",
        }),
      );
      return;
    }

    const plan = plans.find((p) => p.id === planId);
    toast({
      title: `Learn More: ${plan?.name || "Membership"}`,
      description: "Detailed plan information would be shown here.",
    });
  };

  return {
    selectedPlanId,
    handlePlanSelection,
    handleLearnMore,
    isDowngradeConfirmModalOpen,
    setIsDowngradeConfirmModalOpen,
    pendingDowngradePlan,
    handleDowngrade,
    isWarningModalOpen,
    warningModalData,
    handleWarningModalClose,
    showWarningModal,
  };
};
